import { useState, useEffect } from 'react'
import { fmtDate } from '../../utils/format'
import DateInput from '../../components/DateInput'
import QRCode from 'qrcode'
import {
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  CheckCircleIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  QrCodeIcon,
  PrinterIcon
} from '@heroicons/react/24/outline'

interface Refrigerator {
  id: number
  name: string
  code: string
  type: 'positive_cold' | 'negative_cold' | 'maquinaria'
  type_name: string
  location: string
  brand?: string
  model?: string
  serial_number?: string
  min_temperature: number
  max_temperature: number
  target_temperature?: number
  temperature_range: string
  capacity_liters?: number
  installation_date?: string
  status: 'active' | 'maintenance' | 'inactive'
  status_name: string
  notes?: string
  zone_id?: number
  zone_name?: string
  zone_code?: string
  cleaning_frequency_id?: number
  cleaning_frequency_name?: string
  maintenance_frequency_id?: number
  maintenance_frequency_name?: string
  maintenance_due: boolean
  created_at: string
  updated_at: string
}

interface Zone {
  id: number
  name: string
  code: string
  type: string
  status: string
}

interface CleaningFrequency {
  id: number
  code: string
  name: string
  description: string
  hours: number
  is_active: boolean
}

interface MaintenanceFrequency {
  id: number
  code: string
  name: string
  description: string
  months: number
  is_active: boolean
}

const API_BASE_URL = '/api/v1/dev'

export default function Equipment() {
  const [equipment, setEquipment] = useState<Refrigerator[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedEquipment, setSelectedEquipment] = useState<Refrigerator | null>(null)
  const [editingEquipment, setEditingEquipment] = useState<Refrigerator | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showQRModal, setShowQRModal] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [zones, setZones] = useState<Zone[]>([])
  const [cleaningFrequencies, setCleaningFrequencies] = useState<CleaningFrequency[]>([])
  const [maintenanceFrequencies, setMaintenanceFrequencies] = useState<MaintenanceFrequency[]>([])

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'positive_cold',
    brand: '',
    model: '',
    serial_number: '',
    min_temperature: 0,
    max_temperature: 4,
    target_temperature: '',
    capacity_liters: '',
    installation_date: '',
    status: 'active',
    zone_id: '',
    cleaning_frequency_id: '',
    maintenance_frequency_id: '',
    notes: ''
  })

  // Fetch equipment
  const fetchEquipment = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE_URL}/refrigerators`)
      const data = await response.json()

      if (response.ok) {
        setEquipment(data.data || [])
      } else {
        setError('Error al cargar equipos')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching equipment:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch zones
  const fetchZones = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/zones/options`)
      const data = await response.json()
      if (response.ok) {
        setZones(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching zones:', err)
    }
  }

  // Fetch cleaning frequencies
  const fetchCleaningFrequencies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/refrigerators/cleaning-frequencies`)
      const data = await response.json()
      if (response.ok) {
        setCleaningFrequencies(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching cleaning frequencies:', err)
    }
  }

  // Fetch maintenance frequencies
  const fetchMaintenanceFrequencies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/refrigerators/maintenance-frequencies`)
      const data = await response.json()
      if (response.ok) {
        setMaintenanceFrequencies(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching maintenance frequencies:', err)
    }
  }

  useEffect(() => {
    fetchEquipment()
    fetchZones()
    fetchCleaningFrequencies()
    fetchMaintenanceFrequencies()
  }, [])

  // Filter equipment
  const filteredEquipment = equipment.filter(item => {
    const typeMatch = typeFilter === 'all' || item.type === typeFilter
    const statusMatch = statusFilter === 'all' || item.status === statusFilter
    return typeMatch && statusMatch
  })

  // Get type badge
  const getTypeBadge = (type: string) => {
    const typeConfig = {
      positive_cold: { color: 'bg-primary-50 text-primary-700 border border-primary-200', text: 'Frío Positivo' },
      negative_cold: { color: 'bg-accent-50 text-accent-700 border border-accent-200', text: 'Frío Negativo' },
      maquinaria: { color: 'bg-orange-50 text-orange-700 border border-orange-200', text: 'Maquinaria' }
    }

    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.positive_cold

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <BeakerIcon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    )
  }

  // Get status badge
  const getStatusBadge = (status: string, maintenanceDue: boolean = false) => {
    const statusConfig = {
      active: { color: 'bg-green-50 text-green-700 border border-green-200', icon: CheckCircleIcon, text: 'Activo' },
      maintenance: { color: 'bg-accent-50 text-accent-700 border border-accent-200', icon: WrenchScrewdriverIcon, text: 'Mantenimiento' },
      inactive: { color: 'bg-gray-50 text-gray-600 border border-gray-200', icon: ClockIcon, text: 'Inactivo' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active
    const Icon = config.icon

    return (
      <div className="flex items-center space-x-2">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
          <Icon className="w-3 h-3 mr-1" />
          {config.text}
        </span>
        {maintenanceDue && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Mant. Vencido
          </span>
        )}
      </div>
    )
  }

  // Handle create or update
  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const url = editingEquipment
        ? `${API_BASE_URL}/refrigerators/${editingEquipment.id}`
        : `${API_BASE_URL}/refrigerators`

      const method = editingEquipment ? 'PUT' : 'POST'

      const payload = {
        ...formData,
        target_temperature: formData.target_temperature ? parseFloat(formData.target_temperature) : null,
        capacity_liters: formData.capacity_liters ? parseInt(formData.capacity_liters) : null,
        installation_date: formData.installation_date || null,
        zone_id: parseInt(formData.zone_id),
        cleaning_frequency_id: formData.cleaning_frequency_id ? parseInt(formData.cleaning_frequency_id) : null,
        maintenance_frequency_id: formData.maintenance_frequency_id ? parseInt(formData.maintenance_frequency_id) : null
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (response.ok) {
        await fetchEquipment()
        setShowCreateModal(false)
        setEditingEquipment(null)
        resetForm()
      } else {
        if (data.errors) {
          const messages = Object.values(data.errors).flat().join('\n')
          setError(messages)
        } else {
          setError(data.message || 'Error al guardar el equipo')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving equipment:', err)
    } finally {
      setSaving(false)
    }
  }

  // Delete equipment
  const deleteEquipment = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este equipo?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/refrigerators/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchEquipment()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al eliminar el equipo')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting equipment:', err)
    }
  }

  // View equipment details
  const viewEquipment = (equipment: Refrigerator) => {
    setSelectedEquipment(equipment)
    setShowViewModal(true)
  }

  // Open create modal
  const openCreateModal = () => {
    setEditingEquipment(null)
    resetForm()
    setShowCreateModal(true)
    setError(null)
  }

  // Edit equipment
  const editEquipment = (equipment: Refrigerator) => {
    setEditingEquipment(equipment)
    setFormData({
      name: equipment.name,
      code: equipment.code,
      type: equipment.type,
      brand: equipment.brand || '',
      model: equipment.model || '',
      serial_number: equipment.serial_number || '',
      min_temperature: equipment.min_temperature,
      max_temperature: equipment.max_temperature,
      target_temperature: equipment.target_temperature?.toString() || '',
      capacity_liters: equipment.capacity_liters?.toString() || '',
      installation_date: equipment.installation_date || '',
      status: equipment.status,
      zone_id: equipment.zone_id?.toString() || '',
      cleaning_frequency_id: equipment.cleaning_frequency_id?.toString() || '',
      maintenance_frequency_id: equipment.maintenance_frequency_id?.toString() || '',
      notes: equipment.notes || ''
    })
    setShowCreateModal(true)
    setError(null)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      type: 'positive_cold',
      brand: '',
      model: '',
      serial_number: '',
      min_temperature: 0,
      max_temperature: 4,
      target_temperature: '',
      capacity_liters: '',
      installation_date: '',
      status: 'active',
      zone_id: '',
      cleaning_frequency_id: '',
      maintenance_frequency_id: '',
      notes: ''
    })
  }

  // Update temperature ranges based on type
  const updateTemperatureRanges = (type: string) => {
    const ranges = {
      positive_cold: { min: 0, max: 4 },
      negative_cold: { min: -25, max: -18 },
      maquinaria: { min: 0, max: 0 }
    }

    const range = ranges[type as keyof typeof ranges] || ranges.positive_cold

    if (type === 'maquinaria') {
      setFormData({
        ...formData,
        type,
        min_temperature: 0,
        max_temperature: 0,
        target_temperature: ''
      })
    } else {
      setFormData({
        ...formData,
        type,
        min_temperature: range.min,
        max_temperature: range.max,
        target_temperature: ''
      })
    }
  }

  // Generate QR code for equipment
  const generateQRCode = async (equipment: Refrigerator) => {
    try {
      const qrData = {
        equipmentId: equipment.id,
        code: equipment.code,
        name: equipment.name,
        type: equipment.type_name,
        zone: equipment.zone_name,
        temperatureRange: equipment.temperature_range,
        url: `${window.location.origin}/equipment/${equipment.id}`
      }

      const qrString = JSON.stringify(qrData)
      const url = await QRCode.toDataURL(qrString, {
        width: 256,
        margin: 2,
        color: {
          dark: '#ec4899',
          light: '#ffffff'
        }
      })

      setQrCodeUrl(url)
      setSelectedEquipment(equipment)
      setShowQRModal(true)
    } catch (error) {
      console.error('Error generating QR code:', error)
      setError('Error al generar código QR')
    }
  }

  // Print QR code
  const printQRCode = () => {
    if (qrCodeUrl && selectedEquipment) {
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>QR Code - ${selectedEquipment.name}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  text-align: center;
                  margin: 40px;
                }
                .equipment-info {
                  margin: 20px 0;
                  font-size: 14px;
                }
                .qr-code {
                  margin: 20px 0;
                }
                .footer {
                  margin-top: 30px;
                  font-size: 12px;
                  color: #666;
                }
              </style>
            </head>
            <body>
              <h2>Equipo</h2>
              <div class="equipment-info">
                <strong>${selectedEquipment.name}</strong><br/>
                Código: ${selectedEquipment.code}<br/>
                Tipo: ${selectedEquipment.type_name}<br/>
                Zona: ${selectedEquipment.zone_name}<br/>
                ${selectedEquipment.type !== 'maquinaria' ? `Rango: ${selectedEquipment.temperature_range}` : ''}
              </div>
              <div class="qr-code">
                <img src="${qrCodeUrl}" alt="QR Code" />
              </div>
              <div class="footer">
                Pavlova - Sistema APPCC<br/>
                ${fmtDate(new Date())}
              </div>
            </body>
          </html>
        `)
        printWindow.document.close()
        printWindow.print()
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestiona los equipos y maquinaria del sistema APPCC
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nuevo Equipo
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Maintenance Alerts */}
      {equipment.filter(item => item.maintenance_due).length > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
          <div className="flex">
            <WrenchScrewdriverIcon className="h-5 w-5 text-orange-400" />
            <div className="ml-3">
              <p className="text-sm text-orange-700 font-medium">
                Alertas de Mantenimiento
              </p>
              <p className="mt-2 text-sm text-orange-600">
                {equipment.filter(item => item.maintenance_due).length} equipo(s) requieren mantenimiento:
              </p>
              <ul className="mt-2 list-disc list-inside text-sm text-orange-600">
                {equipment
                  .filter(item => item.maintenance_due)
                  .map(item => (
                    <li key={item.id}>
                      <strong>{item.name}</strong> ({item.code}) - Zona: {item.zone_name}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Tipo de equipo</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              <option value="positive_cold">Frío Positivo</option>
              <option value="negative_cold">Frío Negativo</option>
              <option value="maquinaria">Maquinaria</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              <option value="active">Activo</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Equipment Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredEquipment.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay equipos</h3>
          <p className="mt-1 text-sm text-gray-500">Comienza agregando un nuevo equipo.</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Nuevo Equipo
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Equipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zona
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rango de Temperatura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEquipment.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.code}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTypeBadge(item.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.zone_name}</div>
                    <div className="text-xs text-gray-500">{item.zone_code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.type === 'maquinaria' ? (
                      <div className="text-sm text-gray-500">N/A</div>
                    ) : (
                      <div>
                        <div className="text-sm text-gray-900">
                          {parseFloat(String(item.min_temperature)).toFixed(1)}°C a {parseFloat(String(item.max_temperature)).toFixed(1)}°C
                        </div>
                        {item.target_temperature && (
                          <div className="text-xs text-gray-500">
                            Objetivo: {parseFloat(String(item.target_temperature)).toFixed(1)}°C
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(item.status, item.maintenance_due)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => viewEquipment(item)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => generateQRCode(item)}
                        className="text-accent-600 hover:text-accent-900"
                        title="Generar código QR"
                      >
                        <QrCodeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => editEquipment(item)}
                        className="text-green-600 hover:text-green-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteEquipment(item.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingEquipment ? 'Editar Equipo' : 'Nuevo Equipo'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Código *
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tipo *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => updateTemperatureRanges(e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="positive_cold">Frío Positivo</option>
                        <option value="negative_cold">Frío Negativo</option>
                        <option value="maquinaria">Maquinaria</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Estado *
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="active">Activo</option>
                        <option value="maintenance">Mantenimiento</option>
                        <option value="inactive">Inactivo</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Zona *
                    </label>
                    <select
                      value={formData.zone_id}
                      onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      required
                    >
                      <option value="">Selecciona una zona</option>
                      {zones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name} ({zone.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Frecuencia de Limpieza
                      </label>
                      <select
                        value={formData.cleaning_frequency_id}
                        onChange={(e) => setFormData({ ...formData, cleaning_frequency_id: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      >
                        <option value="">Sin frecuencia definida</option>
                        {cleaningFrequencies.map((frequency) => (
                          <option key={frequency.id} value={frequency.id}>
                            {frequency.name} - {frequency.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Frecuencia de Mantenimiento
                      </label>
                      <select
                        value={formData.maintenance_frequency_id}
                        onChange={(e) => setFormData({ ...formData, maintenance_frequency_id: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      >
                        <option value="">Sin frecuencia definida</option>
                        {maintenanceFrequencies.map((frequency) => (
                          <option key={frequency.id} value={frequency.id}>
                            {frequency.name} - {frequency.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Marca
                      </label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Modelo
                      </label>
                      <input
                        type="text"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Número de Serie
                      </label>
                      <input
                        type="text"
                        value={formData.serial_number}
                        onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                  </div>

                  {formData.type !== 'maquinaria' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Temp. Mínima (°C) *
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.min_temperature}
                          onChange={(e) => setFormData({ ...formData, min_temperature: parseFloat(e.target.value) })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Temp. Máxima (°C) *
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.max_temperature}
                          onChange={(e) => setFormData({ ...formData, max_temperature: parseFloat(e.target.value) })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Temp. Objetivo (°C)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={formData.target_temperature}
                          onChange={(e) => setFormData({ ...formData, target_temperature: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Capacidad (Litros)
                      </label>
                      <input
                        type="number"
                        value={formData.capacity_liters}
                        onChange={(e) => setFormData({ ...formData, capacity_liters: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha de Instalación
                      </label>
                      <DateInput
                        value={formData.installation_date}
                        onChange={(v) => setFormData({ ...formData, installation_date: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Notas adicionales..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : (editingEquipment ? 'Actualizar' : 'Crear')}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedEquipment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalles del Equipo: {selectedEquipment.name}
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Código</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedEquipment.code}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Tipo</p>
                      <div className="mt-1">
                        {getTypeBadge(selectedEquipment.type)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Zona</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedEquipment.zone_name}</p>
                      <p className="text-xs text-gray-500">{selectedEquipment.zone_code}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Estado</p>
                      <div className="mt-1">
                        {getStatusBadge(selectedEquipment.status, selectedEquipment.maintenance_due)}
                      </div>
                    </div>
                  </div>

                  {(selectedEquipment.cleaning_frequency_name || selectedEquipment.maintenance_frequency_name) && (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedEquipment.cleaning_frequency_name && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Frecuencia de Limpieza</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedEquipment.cleaning_frequency_name}</p>
                        </div>
                      )}
                      {selectedEquipment.maintenance_frequency_name && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Frecuencia de Mantenimiento</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedEquipment.maintenance_frequency_name}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {(selectedEquipment.brand || selectedEquipment.model || selectedEquipment.serial_number) && (
                    <div className="grid grid-cols-3 gap-4">
                      {selectedEquipment.brand && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Marca</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedEquipment.brand}</p>
                        </div>
                      )}
                      {selectedEquipment.model && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Modelo</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedEquipment.model}</p>
                        </div>
                      )}
                      {selectedEquipment.serial_number && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Número de Serie</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedEquipment.serial_number}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedEquipment.type !== 'maquinaria' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Temperatura Mínima</p>
                        <p className="mt-1 text-sm text-gray-900">{parseFloat(String(selectedEquipment.min_temperature)).toFixed(1)}°C</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Temperatura Máxima</p>
                        <p className="mt-1 text-sm text-gray-900">{parseFloat(String(selectedEquipment.max_temperature)).toFixed(1)}°C</p>
                      </div>
                      {selectedEquipment.target_temperature && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Temperatura Objetivo</p>
                          <p className="mt-1 text-sm text-gray-900">{parseFloat(String(selectedEquipment.target_temperature)).toFixed(1)}°C</p>
                        </div>
                      )}
                    </div>
                  )}

                  {(selectedEquipment.capacity_liters || selectedEquipment.installation_date) && (
                    <div className="grid grid-cols-3 gap-4">
                      {selectedEquipment.capacity_liters && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Capacidad</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedEquipment.capacity_liters} L</p>
                        </div>
                      )}
                      {selectedEquipment.installation_date && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Fecha de Instalación</p>
                          <p className="mt-1 text-sm text-gray-900">
                            {fmtDate(selectedEquipment.installation_date)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedEquipment.notes && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Notas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedEquipment.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedEquipment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-md w-full">
              <div className="bg-white px-6 pt-5 pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Código QR - {selectedEquipment.name}
                  </h3>
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="text-center">
                  {qrCodeUrl && (
                    <div className="space-y-4">
                      <img
                        src={qrCodeUrl}
                        alt="QR Code"
                        className="mx-auto border-2 border-primary-200 rounded-lg"
                      />
                      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                        <p><strong>Código:</strong> {selectedEquipment.code}</p>
                        <p><strong>Zona:</strong> {selectedEquipment.zone_name}</p>
                        <p><strong>Tipo:</strong> {selectedEquipment.type_name}</p>
                        {selectedEquipment.type !== 'maquinaria' && (
                          <p><strong>Rango:</strong> {selectedEquipment.temperature_range}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-3 flex flex-row-reverse space-x-reverse space-x-2">
                <button
                  onClick={printQRCode}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  Imprimir
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}